/**
 * Realtime connection singleton (Phases 1–5 of the unified realtime spec).
 *
 * One socket per app instance, owned here and nowhere else:
 *
 *   startRealtime()  → GET /me/realtime/bootstrap → apply snapshot counts →
 *                      new Pusher(...) → subscribe(personal_channel) →
 *                      bind(EVENT_NAME) → drain GET /me/notifications/changes
 *   frame            → parseRealtimeEnvelope → session binding → dedupe(event_id)
 *                      → coalesced dispatch (+ toast / device alert when LIVE)
 *   stopRealtime()   → unbind everything, disconnect, reset the store
 *
 * Lifecycle is driven from outside: `App.vue` starts it once the session is
 * authenticated on a non-public route and stops it otherwise;
 * `session.store.signOutLocal()` stops it too, so a sign-out never leaves a
 * socket receiving the previous user's frames.
 *
 * CATCH-UP. The server keeps a durable per-user stream; the client holds an
 * opaque signed CURSOR (persisted per tenant + user in `localStorage`) and,
 * whenever the socket (re)subscribes, on return to the foreground / network
 * and every 10 minutes while connected, walks `changes` from it until
 * `has_more` is false. Drained events pass through the same gate as live
 * frames but never toast or alert — the badge and any open page repaint once
 * at the end. 410 / 422 mean the cursor is unusable: drop it and re-bootstrap
 * for a fresh snapshot. Live frames never advance the cursor: only a drain
 * does, so a frame the gateway dropped can never be skipped over.
 *
 * CLOSE CODES. pusher-js treats 4000–4099 as refused (it disconnects and
 * stops); 4009 in particular is the server revoking THIS socket (logout
 * elsewhere, password/role change, tenant switch) and is NOT a sign-out
 * signal: the client re-bootstraps after a short jitter and lets the API's
 * own 401 decide. 4200 (periodic re-auth) is expected and never an error.
 *
 * Two guards run through every asynchronous continuation:
 *  - `generation` — bumped on every start/stop, so a bootstrap, auth or drain
 *    that resolves after a stop is discarded instead of reviving a closed
 *    socket;
 *  - the SESSION EPOCH (`getSessionEpoch()`), captured before each `await` and
 *    re-checked after, so nothing resolved across a sign-out acts on the new
 *    session (the same rule the token refresher follows — see CLAUDE.md).
 *
 * Content discipline: nothing here logs an envelope, a channel name, a cursor
 * or an auth string. Debug output is limited to a rejection *reason class*.
 * The one exception is DEV builds, where pusher-js's own console log is
 * switched on (`Pusher.logToConsole`) because a refused WebSocket upgrade —
 * e.g. the gateway's Origin allowlist answering 403 — is otherwise invisible;
 * see `devOriginMismatch()` and CLAUDE.md ("Open the app at http://localhost:5173").
 *
 * PHASE 6 (phase6-contract.md §3.1) keeps that structure and adds, exactly:
 *  - a build-flag kill switch (`REALTIME_ENABLED` false → `disabled`, no
 *    bootstrap, no socket, the REST poll fallback still runs);
 *  - an offline pause (the `isOffline` true edge / `offline`) and a
 *    reachability-edge resume (`nudge()` + a receipt flush + an outbox flush);
 *  - `reconcile(reason)` — a throttled REST refresh of both stores on a
 *    rejected / mismatched frame, a drain that gave up or had rejections, and
 *    the device boundary's push reasons — exported, never rendering;
 *  - `appliedSequence`, the as-of guard on every unread count, ordered bursts
 *    handed to the two reducers (`applyEvent`) with ONE refresh per store per
 *    burst only when a reducer answered `'unresolved'`;
 *  - the ERPat refusal cadences: 4009 keeps the 2–5 s jitter; every other
 *    4001–4099 close re-bootstraps first after the jitter, then at most once
 *    per `REFUSED_REBOOTSTRAP_MIN_INTERVAL_MS` (DEC-079) — a cadence the wake
 *    `nudge()` respects; a refused `changes` page keeps its cursor until the
 *    re-bootstrap actually runs, at most once per
 *    `DRAIN_REFUSAL_REBOOTSTRAP_MIN_INTERVAL_MS` (DEC-080); a drain never
 *    issues a cursorless request;
 *  - `recordOpened(eventId)` — an `opened` receipt parked in a bounded queue
 *    until the binding says receipts are enabled;
 *  - the session identity snapshot (`services/session-scope.ts`) recorded on
 *    `bindSession()`, and `stopRealtime({forget: true})`, which removes the
 *    persisted cursor(s) at sign-out (DEC-083) — the route-driven stop keeps
 *    `forget: false`;
 *  - the toast action goes through `navigateToRealtimeTarget()` (§3.7).
 *
 * GATEWAY GUARDS (DEC-102, `utils/realtime-gateway.ts`) run on every bootstrap,
 * between the version check and `openSocket()`. The socket target stays
 * server-driven — Syntry has no host/port/TLS override — and exactly two
 * verdicts exist:
 *  - `gateway_loopback` — the bootstrap names a loopback `ws_host` while the
 *    API base is a remote host ("a dev `.env` copied to production": every
 *    device would dial itself). HARD: status `failed`, no socket, the REST
 *    poll fallback on; the wake `nudge()` re-checks the verdict through the
 *    `gatewayRefused` latch, so a corrected server is picked up on the next
 *    foreground / network wake;
 *  - `gateway_unexpected` — the bootstrap's normalised `ws(s)://host[:port]`
 *    is not in the optional ADVISORY pin `VITE_REALTIME_GATEWAY_ORIGINS`.
 *    Named once in the dev console and as the store's `lastError`, and the
 *    socket is STILL dialled — a successful connect clears it, exactly like
 *    `origin_mismatch`. A wrong pin costs nothing; a wrong server produces a
 *    named line on the Live-updates card instead of an endless "Reconnecting…".
 */
import Pusher, { type Channel, type ChannelAuthorizationCallback } from 'pusher-js';
import { watch, type WatchStopHandle } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import { getSessionEpoch, hasAccessToken } from '@/services/api/erpat-client';
import {
  RealtimeApiError,
  authorizeChannel,
  fetchBootstrap,
  fetchChanges,
  installationId,
  postReceipts,
  type RealtimeBootstrap,
  type RealtimeReceipt,
  type RealtimeUnreadCounts,
} from '@/services/api/realtime.api';
import {
  EVENT_NAME,
  SUPPORTED_EVENT_VERSIONS,
  explainRealtimeEnvelopeRejection,
  parseRealtimeEnvelope,
  type RealtimeEnvelope,
  type RealtimeEnvelopeType,
} from '@/contracts/realtime-envelope-v1';
import { INBOX_LOCATION, resolveRealtimeTarget } from '@/contracts/realtime-routes';
import {
  CHANGES_PAGE_SIZE,
  CLOSE_CODES_REFUSED,
  CLOSE_CODE_OVER_CAPACITY,
  CLOSE_CODE_REAUTH,
  CLOSE_CODE_RETRY_NOW,
  CURSOR_STORAGE_PREFIX,
  DISPATCH_COALESCE_MS,
  DRAIN_MAX_PAGES,
  DRAIN_REFUSAL_REBOOTSTRAP_MIN_INTERVAL_MS,
  DRAIN_RETRY_ATTEMPTS,
  IDLE_DRAIN_INTERVAL_MS,
  PONG_TIMEOUT_MS,
  RECEIPT_BATCH_MAX,
  RECEIPT_FLUSH_INTERVAL_MS,
  RECONCILE_THROTTLE_MS,
  RECORD_OPENED_QUEUE_MAX,
  REFUSED_REBOOTSTRAP_MIN_INTERVAL_MS,
  UNAVAILABLE_TIMEOUT_MS,
  bootstrapRetryDelayMs,
  drainRetryDelayMs,
  refusedRebootstrapDelayMs,
} from '@/contracts/realtime-policy';
import {
  NOTIFICATION_DEDUPE_SIZE,
  NOTIFICATION_POLL_FALLBACK_MS,
  NOTIFICATION_TOAST_DURATION_MS,
} from '@/config/notifications';
import { API_BASE_URL, IS_DEV, REALTIME_ENABLED, REALTIME_GATEWAY_ORIGINS } from '@/utils/env';
import { gatewayRefusal, type GatewayRefusal } from '@/utils/realtime-gateway';
import { compareStreamSequence } from '@/services/api/models';
import { isOffline } from '@/services/connectivity';
import { navigateToRealtimeTarget } from '@/services/realtime-navigation';
import { getSessionScope, isSelfActor, setSessionScope, tenantStamp } from '@/services/session-scope';
import { playNotificationChime } from '@/services/notification-sound';
import { useRealtimeStore, type RealtimeStatus, type RealtimeTick } from '@/stores/realtime.store';
import { useNotificationsStore } from '@/stores/notifications.store';
import { useMessagesStore } from '@/stores/messages.store';
import { notificationsDevice } from '@/services/device/notifications.service';
import { useToast, type ToastOverrides } from '@/composables/useToast';
import { burstCopy, messageAlertCopy, notificationCopy, privateFallbackCopy } from '@/i18n/notification-copy';
import type { ReducerOutcome } from '@/types/notification';

/** Why a REST reconcile ran (phase6-contract.md §3.1). Content-free; also a diagnostics label. */
export type ReconcileReason =
  | 'rejected'
  | 'session_mismatch'
  | 'drain_rejections'
  | 'drain_failed'
  | 'push_received'
  | 'push_suppressed'
  | 'unknown_type';

/** `stopRealtime()` options — `forget: true` is the sign-out / company-switch stop (DEC-083). */
export interface StopRealtimeOptions {
  /** Remove the persisted cursor(s) too. `App.vue`'s route-driven stop passes `false`. */
  forget?: boolean;
}

/** The 4009 close: the gateway revoked THIS socket (keeps the 2–5 s jitter). */
const CLOSE_CODE_REVOKED = 4009;
/** Every 4001–4099 close is "refused" by pusher-js (it disconnects and never retries). */
const CLOSE_CODE_REFUSED_MIN = 4001;
const CLOSE_CODE_REFUSED_MAX = 4099;

/* ── De-duplication window ─────────────────────────────────────────────────── */

/** Bounded FIFO set of recent `event_id`s (spec: remember the last 500). */
class RecentIds {
  private readonly ids = new Set<string>();
  private readonly order: string[] = [];

  constructor(private readonly capacity: number) {}

  has(id: string): boolean {
    return this.ids.has(id);
  }

  add(id: string): void {
    if (this.ids.has(id)) return;
    this.ids.add(id);
    this.order.push(id);
    while (this.order.length > this.capacity) {
      const oldest = this.order.shift();
      if (oldest !== undefined) this.ids.delete(oldest);
    }
  }

  clear(): void {
    this.ids.clear();
    this.order.length = 0;
  }
}

/* ── Module state ──────────────────────────────────────────────────────────── */

let active = false;
let generation = 0;
let pusher: Pusher | null = null;
let channel: Channel | null = null;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let retryAttempt = 0;
let refusedTimer: ReturnType<typeof setTimeout> | null = null;
/** True while the pending `refusedTimer` was scheduled by a 4009 (a wake may collapse it). */
let refusedRevoked = false;
/** Epoch ms of the last re-bootstrap after a NON-4009 refused close (the DEC-079 cadence). */
let lastRefusedRebootstrapAt = 0;
let pollTimer: ReturnType<typeof setInterval> | null = null;
let pollIntervalMs = NOTIFICATION_POLL_FALLBACK_MS;
let wakeListenersAttached = false;
/** The `watch(isOffline)` stop handle — released with the wake listeners. */
let offlineWatchStop: WatchStopHandle | null = null;
let originMismatchWarned = false;
/** `REALTIME_ENABLED` is false: `active` with no socket and no bootstrap, polling only. */
let flagDisabled = false;
/** A DEC-102 gateway verdict has been printed for this bootstrap (dev only, one line). */
let gatewayWarned = false;
/**
 * `gateway_loopback` (DEC-102): the bootstrap named a loopback gateway while
 * the API is remote — `active`, status `failed`, no socket, polling only, until
 * a corrected bootstrap. The explicit latch `nudge()` re-checks, so a wake
 * re-bootstraps a refused service the same way it re-checks a `disabled` one.
 */
let gatewayRefused = false;
/** The highest stream sequence applied (decimal string), mirrored into the store. */
let appliedSequence: string | null = null;
let lastReconcileAt = 0;
/** `opened` receipts recorded before the binding is known (bounded; drained on bind). */
const openedQueue: string[] = [];
const recent = new RecentIds(NOTIFICATION_DEDUPE_SIZE);

/** What the current bootstrap bound this connection to. */
interface Binding {
  tenantScope: string;
  recipientId: string;
  /** `localStorage` key of the persisted cursor for this tenant + user. */
  cursorKey: string;
  /** `features.receipts` AND this device has a UUID installation id. */
  receipts: boolean;
  sampleRate: number;
  installation: string | null;
}
let binding: Binding | null = null;
/** The stream position the next drain starts from. Advanced by drains only. */
let cursor: string | null = null;

// Catch-up drain
let draining = false;
let drainQueued = false;
let drainRetryTimer: ReturnType<typeof setTimeout> | null = null;
let drainRetryAttempt = 0;
/** Consecutive cursor-driven re-bootstraps without a completed drain (loop guard). */
let cursorRebootstraps = 0;
/** Epoch ms of the last re-bootstrap after a refused `changes` page (the DEC-080 cadence). */
let lastDrainRefusalRebootstrapAt = 0;
/**
 * Set by a refused `changes` page (403/404/410/422): the cursor stays until the
 * re-bootstrap actually runs, then `bindSession()` drops it so the fresh
 * bootstrap's position replaces it (the persisted one would otherwise win).
 */
let dropCursorOnBind = false;
let idleDrainTimer: ReturnType<typeof setInterval> | null = null;

// Coalesced dispatch
interface Burst {
  /** Live envelopes in this window (drained ones are counted, never toasted). */
  live: RealtimeEnvelope[];
  /** EVERY accepted envelope of the window, kept in `sequence` order — the reducers see each. */
  all: RealtimeEnvelope[];
  last: RealtimeEnvelope;
  timer: ReturnType<typeof setTimeout>;
}
const bursts = new Map<RealtimeEnvelopeType, Burst>();

// Receipts
const receiptQueue: RealtimeReceipt[] = [];
let receiptTimer: ReturnType<typeof setTimeout> | null = null;

/** True while `gen` is still the live attempt and the session it started under is alive. */
function isCurrent(gen: number, epoch: number): boolean {
  return active && gen === generation && getSessionEpoch() === epoch && hasAccessToken();
}

/**
 * Dev-only: is this page open at an origin other than the API's?
 *
 * In dev the contract is same-origin (`VITE_API_BASE_URL` = Vite's own origin,
 * proxied to the backend — see CLAUDE.md), and the realtime gateway checks the
 * WebSocket `Origin` against an exact allowlist before the upgrade. A tab on
 * `:5174` (Vite's silent fallback when `:5173` is taken), a LAN IP or
 * `127.0.0.1` still gets every REST call but is refused a socket with a 403
 * that pusher-js never surfaces — the UI would just say "Reconnecting…"
 * forever. Name it once, in the console and in the store.
 *
 * Production is cross-origin by design (the PWA is not served by the API), so
 * this is never evaluated there.
 */
function devOriginMismatch(): boolean {
  if (!IS_DEV || typeof window === 'undefined') return false;
  let expected: string;
  try {
    expected = new URL(API_BASE_URL).origin;
  } catch {
    return false;
  }
  if (window.location.origin === expected) return false;
  if (!originMismatchWarned) {
    originMismatchWarned = true;
    console.warn(
      `[realtime] Syntry is open at ${window.location.origin} but VITE_API_BASE_URL is ${expected}. ` +
        `Open the app at ${expected}: the realtime gateway's origin allowlist and the same-origin API proxy both depend on it.`,
    );
  }
  return true;
}

/**
 * The PAGE protocol pusher-js keys its TLS decision on (`shouldUseTLS()`:
 * `https:` always dials `wss://…:wssPort`, anything else follows `force_tls`).
 * `window.location.protocol`, never `isSecureContext` — that is not what
 * pusher-js reads, and happy-dom does not implement it.
 */
function pageProtocol(): string {
  return typeof window === 'undefined' ? '' : window.location.protocol;
}

/** The API's hostname, or `null` when `API_BASE_URL` does not parse (the loopback rule then stays inert). */
function apiHostname(): string | null {
  try {
    return new URL(API_BASE_URL).hostname;
  } catch {
    return null;
  }
}

/**
 * Dev-only, once per bootstrap: name a DEC-102 gateway verdict in the console,
 * because the failure it predicts is otherwise invisible — a refused or
 * unreachable upgrade is a bare DOM `error` pusher-js swallows and retries.
 * Hostnames / ports only: never a channel name, a key or a token.
 */
function warnGateway(refusal: GatewayRefusal): void {
  if (!IS_DEV || gatewayWarned) return;
  gatewayWarned = true;
  const named = refusal.origin ?? '(an unusable host)';
  if (refusal.code === 'gateway_loopback') {
    console.warn(
      `[realtime] The bootstrap names a loopback gateway (${named}) while the API is ${apiHostname() ?? 'remote'}: ` +
        'every device would dial itself. Not connecting — check FUXION_WS_HOST on the ERPat server (DEC-102).',
    );
    return;
  }
  console.warn(
    `[realtime] The bootstrap names ${named} but VITE_REALTIME_GATEWAY_ORIGINS expects ${REALTIME_GATEWAY_ORIGINS.join(', ')}. ` +
      'Trying anyway (the pin is advisory, DEC-102) — a refused upgrade would show nothing else.',
  );
}

function asObject(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

/**
 * The reason CLASS of a pusher-js connection error (`type` + numeric `code`),
 * never its message or payload — safe to print in dev.
 */
function connectionErrorClass(err: unknown): string {
  const rec = asObject(err);
  const type = typeof rec.type === 'string' ? rec.type : 'unknown';
  const code = closeCodeOf(err);
  return code !== null ? `${type}#${code}` : type;
}

/**
 * The gateway close code carried by a pusher-js `error` event, or `null`.
 * pusher-js emits a close as `{type:'PusherError', data:{code}}` and wraps a
 * transport failure as `{type:'WebSocketError', error:{…}}`; read both.
 */
function closeCodeOf(err: unknown): number | null {
  const rec = asObject(err);
  const direct = asObject(rec.data);
  if (typeof direct.code === 'number') return direct.code;
  const nested = asObject(asObject(rec.error).data);
  return typeof nested.code === 'number' ? nested.code : null;
}

/* ── Public API ────────────────────────────────────────────────────────────── */

/**
 * Start (idempotent). Safe to call whenever the session becomes authenticated;
 * does nothing without an access token or while already running.
 *
 * With `REALTIME_ENABLED` false (the build-flag kill switch) the service is
 * `active` but opens no socket and never bootstraps: the wake listeners are
 * attached, the status reads `disabled` and the REST poll fallback keeps the
 * badge fresh. `nudge()` never re-bootstraps a flag-disabled service.
 */
export function startRealtime(): void {
  if (active) return;
  if (!hasAccessToken()) return;
  active = true;
  const gen = ++generation;
  const store = useRealtimeStore();
  store.setDedupeSize(NOTIFICATION_DEDUPE_SIZE);
  attachWakeListeners();
  // The device boundary reports an opened alert back here as an `opened`
  // receipt (parked until the binding says receipts are enabled). `prepare()`
  // is boot-level and idempotent (§3.7) — calling it here too only covers a
  // start that raced the boot wiring; the listeners are NOT released at stop.
  notificationsDevice.setOpenedHook((eventId) => recordOpened(eventId));
  notificationsDevice.prepare();
  // The permission facade re-checks at realtime start (§3.5.2).
  notificationsDevice.recheck();
  if (!REALTIME_ENABLED) {
    flagDisabled = true;
    store.setStatus('disabled');
    startPollFallback();
    return;
  }
  void bootstrapAndConnect(gen);
}

/**
 * Stop and forget everything in memory (idempotent). Called on sign-out, on a
 * company switch and on route changes to public pages.
 *
 * `{forget: true}` (the sign-out stop — `session.store.logout()` /
 * `signOutLocal()`) also removes the PERSISTED cursor: the binding's own key
 * and, defensively, every key under `syntry.realtime_cursor:` (DEC-083). A
 * signed per-user artefact must not outlive the session on a shared device.
 * `App.vue`'s route-driven stop passes `false`, so a brief hop through a public
 * route keeps the catch-up position.
 */
export function stopRealtime(options: StopRealtimeOptions = {}): void {
  const forget = options.forget === true;
  active = false;
  flagDisabled = false;
  gatewayWarned = false;
  gatewayRefused = false;
  generation++;
  clearRetry();
  clearRefusedTimer();
  refusedRevoked = false;
  lastRefusedRebootstrapAt = 0;
  stopPollFallback();
  stopIdleDrain();
  clearDrainRetryTimer();
  drainRetryAttempt = 0;
  cursorRebootstraps = 0;
  lastDrainRefusalRebootstrapAt = 0;
  dropCursorOnBind = false;
  clearBursts();
  clearReceipts();
  detachWakeListeners();
  teardownSocket();
  // The bootstrap's host-notifications block is gone with the binding; the
  // facade's listeners stay attached (boot-level — `release()` is app teardown).
  notificationsDevice.applyBootstrap(null);
  if (forget) {
    forgetPersistedCursors();
    // Receipts parked before a binding belonged to this session; a new session
    // must never post them under its own installation.
    openedQueue.length = 0;
  }
  // Otherwise the persisted cursor stays: it is keyed by tenant + user and is
  // what turns the next start into a catch-up. Only the in-memory binding is
  // dropped (and the parked `opened` receipts wait for the next start).
  binding = null;
  cursor = null;
  draining = false;
  drainQueued = false;
  recent.clear();
  appliedSequence = null;
  lastReconcileAt = 0;
  useRealtimeStore().reset();
}

/**
 * A REST reconcile: refresh the notification badge/items and the conversation
 * list from the server, at most once per `RECONCILE_THROTTLE_MS` whatever the
 * reason. Never renders anything. Called on a frame the parser rejected, a
 * session-mismatch drop, a drain that gave up or ended with rejections, and by
 * the device boundary for a push that was received in the foreground or
 * suppressed by the worker. A duplicate `event_id` is NOT a reconcile.
 *
 * Gated on an access token (not on `active`): a push may land before the
 * service started, and a guest session has nothing to refresh.
 */
export function reconcile(reason: ReconcileReason): void {
  if (!hasAccessToken()) return;
  const now = Date.now();
  if (now - lastReconcileAt < RECONCILE_THROTTLE_MS) return;
  lastReconcileAt = now;
  if (IS_DEV) console.debug('[realtime] reconcile:', reason);
  useRealtimeStore().bump('reconciles');
  void useNotificationsStore().refresh();
  void useMessagesStore().refresh();
}

/**
 * Record that the user OPENED an event (a toast action, a device-alert tap, a
 * replayed cold-start deep link). Before the binding is known the id is parked
 * in a bounded queue (`RECORD_OPENED_QUEUE_MAX`, oldest dropped) and drained
 * once the bootstrap says receipts are enabled; with a binding it is queued as
 * a receipt at once (or dropped when receipts are off for this installation).
 * An id already waiting as an `opened` receipt is not queued twice.
 */
export function recordOpened(eventId: string): void {
  if (typeof eventId !== 'string' || eventId === '') return;
  if (binding) {
    if (!binding.receipts) return;
    if (receiptQueue.some((r) => r.type === 'opened' && r.eventId === eventId)) return;
    recordReceipt(eventId, 'opened');
    return;
  }
  if (openedQueue.includes(eventId)) return;
  openedQueue.push(eventId);
  while (openedQueue.length > RECORD_OPENED_QUEUE_MAX) openedQueue.shift();
}

/* ── Bootstrap ─────────────────────────────────────────────────────────────── */

async function bootstrapAndConnect(gen: number): Promise<void> {
  const store = useRealtimeStore();
  clearRefusedTimer();
  // Every bootstrap is a fresh DEC-102 verdict: a refusal is never sticky. The
  // dev-console warning stays a once-per-session latch (like
  // `originMismatchWarned`) — it is reset only in `stopRealtime()` — so a wake
  // that re-bootstraps every minute cannot flood the console.
  gatewayRefused = false;
  store.setStatus('connecting');
  store.setError(null);
  // Flag a wrong dev origin up front: the socket below will be refused at the
  // gateway with nothing else to show for it. Still try — the allowlist may
  // have been widened on purpose — and a successful connect clears it.
  if (devOriginMismatch()) store.setError('origin_mismatch');

  const epoch = getSessionEpoch();
  let bootstrap: RealtimeBootstrap;
  try {
    bootstrap = await fetchBootstrap();
  } catch (err) {
    if (!isCurrent(gen, epoch)) return;
    // The session ended — ERPat refused the token refresh and the teardown ran
    // (the session store stops this service) — or is ending: nothing to
    // schedule. A refresh that merely did not get through is NOT this: it
    // arrives as `refresh_failed` (status 0) and takes the retry path below.
    if (err instanceof RealtimeApiError && (err.code === 'session_expired' || err.code === 'session_changed')) {
      return;
    }
    // Whatever refused us, the inbox and badge keep working over REST.
    startPollFallback();
    if (err instanceof RealtimeApiError && err.status === 404) {
      // A server without the realtime endpoints (a release before the stream
      // existed): a well-formed "off". No socket, no retry hammer — the wake
      // `nudge()` re-checks the verdict on return to the foreground / network.
      store.setStatus('disabled');
      return;
    }
    // The API is unreachable or refused us: report, back off, try again.
    store.setStatus('unavailable');
    store.setError('bootstrap_failed');
    scheduleRetry(gen);
    return;
  }
  if (!isCurrent(gen, epoch)) return;

  retryAttempt = 0;
  store.setEnabled(bootstrap.enabled);
  pollIntervalMs = bootstrap.pollIntervalSeconds * 1_000;
  bindSession(bootstrap);
  // The permission facade adopts the tenant policy + VAPID key and re-checks
  // its registration un-throttled (§3.5.1) — both exist only from here on.
  notificationsDevice.applyBootstrap(bootstrap.hostNotifications);

  if (!bootstrap.enabled || !bootstrap.pusher || !bootstrap.personalChannel) {
    // Tenant has realtime off (or the server sent nothing usable): no socket.
    store.setStatus('disabled');
    return;
  }
  if (!bootstrap.supportedEventVersions.some((v) => SUPPORTED_EVENT_VERSIONS.includes(v))) {
    // A server that only speaks a version we cannot render — refusing to
    // connect is the honest outcome; there would be nothing to show.
    store.setStatus('disabled');
    store.setError('version_mismatch');
    return;
  }

  // The DEC-102 gateway guards (`utils/realtime-gateway.ts`): where the server
  // is pointing us. The one HARD refusal is a loopback gateway named while the
  // API is remote; the pin is advisory and never stops a dial.
  const refusal = gatewayRefusal({
    settings: bootstrap.pusher,
    pageProtocol: pageProtocol(),
    apiHostname: apiHostname(),
    pins: REALTIME_GATEWAY_ORIGINS,
  });
  if (refusal?.code === 'gateway_loopback') {
    // The server named a loopback gateway while the API is remote: every device
    // would dial itself. `failed` (not `disabled` — the server said enabled, and
    // the disabled copy would claim "not enabled for your company"), the REST
    // poll keeps the inbox fresh at the bootstrap's interval, and the wake
    // `nudge()` re-checks the verdict through `gatewayRefused`.
    gatewayRefused = true;
    warnGateway(refusal);
    store.setStatus('failed');
    store.setError('gateway_loopback');
    startPollFallback();
    return;
  }
  if (refusal?.code === 'gateway_unexpected') {
    // Advisory (DEC-102): name it, then still try — a successful connect clears
    // it (as `origin_mismatch`). If the dial fails, the card now says why.
    warnGateway(refusal);
    store.setError('gateway_unexpected');
  }

  openSocket(bootstrap, bootstrap.pusher, bootstrap.personalChannel, gen);
}

/**
 * Bind this connection to the tenant + recipient the server answered for, and
 * pick the stream position to resume from.
 *
 * The snapshot's counts are authoritative for "now"; the first drain brings
 * the stream up to the same point. A cursor persisted by a previous session
 * (same tenant, same user) wins over the bootstrap's fresh one — that is what
 * turns "the app was closed for an hour" into a catch-up instead of a gap.
 */
function bindSession(bootstrap: RealtimeBootstrap): void {
  const store = useRealtimeStore();
  const installation = installationId();
  const same =
    binding !== null &&
    binding.tenantScope === bootstrap.tenantScope &&
    binding.recipientId === bootstrap.recipientId;
  binding = {
    tenantScope: bootstrap.tenantScope,
    recipientId: bootstrap.recipientId,
    cursorKey: `${CURSOR_STORAGE_PREFIX}:${bootstrap.tenantScope}:${bootstrap.recipientId}`,
    receipts: bootstrap.features.receipts && installation !== null,
    sampleRate: bootstrap.hostNotifications.receiptSampleRate,
    installation,
  };
  // The session identity snapshot (§3.1): the company key of this session and
  // the recipient the server bound us to. Every identity-keyed clear at
  // sign-out reads THIS, never the token — the expiry path clears the tokens
  // before `signOutLocal()` runs.
  setSessionScope({ userId: bootstrap.recipientId });
  if (!same) {
    recent.clear();
    cursor = null;
  }
  if (dropCursorOnBind) {
    // A refused `changes` page asked for a fresh position: drop the one the
    // server refused now that the re-bootstrap has actually answered, so its
    // cursor below replaces it instead of the persisted one winning again.
    dropCursorOnBind = false;
    clearCursor();
  }
  if (bootstrap.snapshot) {
    const asOf = bootstrap.snapshot.asOfSequence;
    if (isAtOrPastApplied(asOf)) {
      // The snapshot REPLACES the items it carries; its counts are as of the
      // same sequence. Items first, so the count derives its verdict from them.
      useNotificationsStore().applySnapshot(bootstrap.snapshot.notifications, asOf);
      applyUnreadCounts(bootstrap.snapshot.unreadCounts, asOf);
    }
  }

  const persisted = loadCursor(binding.cursorKey);
  if (persisted) {
    cursor = persisted;
  } else if (bootstrap.cursor) {
    setCursor(bootstrap.cursor);
  } else {
    cursor = null;
  }
  store.setHasCursor(cursor !== null);
  store.setSyncState('idle');

  // `opened` receipts parked before this binding existed: post them now that
  // the features are known, or drop them when receipts are off here (ERPat
  // parity — a receipt is telemetry, never retried across bindings).
  const parked = openedQueue.splice(0, openedQueue.length);
  if (binding.receipts) {
    for (const eventId of parked) recordReceipt(eventId, 'opened');
  }
}

/** Advance the applied stream position (decimal string compare) when `sequence` is newer. */
function advanceAppliedSequence(sequence: string | null | undefined): void {
  if (typeof sequence !== 'string' || !/^[0-9]{1,20}$/.test(sequence)) return;
  if (appliedSequence === null || compareStreamSequence(sequence, appliedSequence) > 0) {
    appliedSequence = sequence;
    useRealtimeStore().setAppliedSequence(sequence);
  }
}

/** `true` when a count / snapshot consistent as of `asOf` is at least as new as everything applied. */
function isAtOrPastApplied(asOf: string): boolean {
  if (!/^[0-9]{1,20}$/.test(asOf)) return false;
  return appliedSequence === null || compareStreamSequence(asOf, appliedSequence) >= 0;
}

function scheduleRetry(gen: number): void {
  // Timer only — the attempt counter must survive between failures or the
  // exponential backoff never grows past its first step (a flat 5 s hammer on
  // a backend that is already struggling). It is zeroed on success and on stop.
  clearRetryTimer();
  retryAttempt += 1;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    if (!active || gen !== generation) return;
    void bootstrapAndConnect(gen);
  }, bootstrapRetryDelayMs(retryAttempt));
}

function clearRetryTimer(): void {
  if (retryTimer !== null) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
}

/** Forget the pending retry AND the backoff position (stop / success). */
function clearRetry(): void {
  clearRetryTimer();
  retryAttempt = 0;
}

function clearRefusedTimer(): void {
  if (refusedTimer !== null) {
    clearTimeout(refusedTimer);
    refusedTimer = null;
  }
}

/* ── Socket ────────────────────────────────────────────────────────────────── */

/**
 * pusher-js appends `/app/<key>` itself; `wsPath` is a PREFIX in front of it.
 * The bootstrap's `ws_path` describes the gateway's route (`/app`), so passing
 * it verbatim would dial `/app/app/<key>`. Strip that suffix and pass only
 * whatever prefix remains (empty in every current deployment).
 */
function wsPathPrefix(wsPath: string): string {
  return wsPath.replace(/\/app\/?$/, '').replace(/\/+$/, '');
}

function toStatus(pusherState: string): RealtimeStatus {
  switch (pusherState) {
    case 'connected':
      return 'connected';
    case 'unavailable':
      return 'unavailable';
    case 'failed':
      return 'failed';
    case 'disconnected':
      return 'disconnected';
    default:
      // 'initialized' | 'connecting'
      return 'connecting';
  }
}

/** Unbind and drop the pusher instance (does NOT bump the generation). */
function teardownSocket(): void {
  if (channel) {
    channel.unbind_all();
    channel = null;
  }
  if (pusher) {
    pusher.connection.unbind_all();
    pusher.unbind_all();
    pusher.disconnect();
    pusher = null;
  }
}

function openSocket(
  bootstrap: RealtimeBootstrap,
  settings: NonNullable<RealtimeBootstrap['pusher']>,
  personalChannel: string,
  gen: number,
): void {
  const store = useRealtimeStore();

  // Channel authorization is delegated to our own transport (form-encoded
  // POST with bearer + X-Company-Key, one-shot 401 refresh). `transport` and
  // `endpoint` are required by the option type but unused once `customHandler`
  // is present; the endpoint value is the server-declared one for clarity.
  const customHandler = (
    params: { socketId: string; channelName: string },
    callback: ChannelAuthorizationCallback,
  ): void => {
    const epoch = getSessionEpoch();
    authorizeChannel(params.socketId, params.channelName)
      .then((auth) => {
        if (!isCurrent(gen, epoch)) {
          callback(new Error('realtime_stale'), null);
          return;
        }
        callback(null, auth);
      })
      .catch((err: unknown) => {
        if (isCurrent(gen, epoch)) store.setError('channel_auth_failed');
        callback(err instanceof Error ? err : new Error('channel_auth_failed'), null);
      });
  };

  const prefix = wsPathPrefix(settings.wsPath);
  // pusher-js's own transport/strategy log — the only place a refused upgrade
  // (an origin 403, a 429) is visible, since the connection manager never
  // re-emits transport-phase errors. Dev only; it prints frame metadata but
  // production users must never have channel names in their console.
  Pusher.logToConsole = IS_DEV;
  const client = new Pusher(settings.appKey, {
    // `cluster` is mandatory in the option type; it is ignored once wsHost is set.
    cluster: 'local',
    wsHost: settings.wsHost,
    wsPort: settings.wsPort,
    wssPort: settings.wssPort,
    forceTLS: settings.forceTls,
    ...(prefix ? { wsPath: prefix } : {}),
    enabledTransports: ['ws', 'wss'],
    disableStats: true,
    activityTimeout: settings.activityTimeoutSeconds * 1_000,
    pongTimeout: PONG_TIMEOUT_MS,
    unavailableTimeout: UNAVAILABLE_TIMEOUT_MS,
    channelAuthorization: {
      transport: 'ajax',
      endpoint: bootstrap.authEndpoint,
      customHandler,
    },
  });
  pusher = client;

  client.connection.bind('state_change', (change: { previous: string; current: string }) => {
    if (gen !== generation) return;
    const status = toStatus(change.current);
    store.setStatus(status);
    if (status === 'connected') {
      store.setSocketId(client.connection.socket_id);
      store.setError(null);
      stopPollFallback();
      // Anything queued while the socket was down goes out now (the store
      // throttles this walk by `OUTBOX_FLUSH_MIN_INTERVAL_MS`).
      useMessagesStore().flushOutbox();
    } else if (status === 'unavailable' || status === 'failed') {
      // pusher-js is retrying (or gave up): keep the inbox fresh by polling
      // until the socket is back.
      startPollFallback();
    }
  });
  client.connection.bind('error', (err: unknown) => {
    if (gen !== generation) return;
    handleConnectionError(err, gen);
  });

  const subscription = client.subscribe(personalChannel);
  channel = subscription;
  subscription.bind('pusher:subscription_succeeded', () => {
    if (gen !== generation) return;
    store.setError(null);
    // Subscribed: anything that happened while we were away is in the stream.
    startIdleDrain(gen);
    void drain(gen);
  });
  subscription.bind('pusher:subscription_error', () => {
    if (gen === generation) store.setError('subscription_error');
  });
  subscription.bind(EVENT_NAME, (data: unknown) => {
    if (gen !== generation) return;
    handleFrame(data, false);
  });
}

/**
 * Route a pusher-js `error` event by its close code (see the policy file).
 * Only the reason class is kept; the payload is never logged in production.
 */
function handleConnectionError(err: unknown, gen: number): void {
  const store = useRealtimeStore();
  const code = closeCodeOf(err);
  if (IS_DEV) console.debug('[realtime] connection error:', connectionErrorClass(err));

  // The listed refusals (4001/4007/4009) AND every other 4001–4099 close:
  // pusher-js answers all of them with disconnect() and no retry, so each is
  // ours to re-bootstrap — 4009 on its jitter, the rest on the DEC-079 cadence.
  if (
    code !== null &&
    (CLOSE_CODES_REFUSED.includes(code) || (code >= CLOSE_CODE_REFUSED_MIN && code <= CLOSE_CODE_REFUSED_MAX))
  ) {
    handleRefusedClose(gen, code);
    return;
  }
  if (code === CLOSE_CODE_REAUTH || code === CLOSE_CODE_RETRY_NOW) {
    // Expected: the gateway asks for a fresh handshake and pusher-js
    // reconnects at once. Not an error state — the dot must not flicker.
    return;
  }
  if (code === CLOSE_CODE_OVER_CAPACITY) {
    // pusher-js backs off on its own; keep the inbox fresh meanwhile.
    store.setError('connection_error');
    startPollFallback();
    return;
  }
  store.setError('connection_error');
}

/**
 * A refused close (4001 / 4007 / 4009, or any other 4001–4099). pusher-js has
 * already called `disconnect()` and will NOT retry, so: drop the instance (so
 * `nudge()` can never `connect()` a socket the server just revoked), poll
 * meanwhile, and re-bootstrap. The bootstrap answers with the current verdict
 * — still enabled → a new socket; tenant switched or realtime turned off →
 * `disabled`; session revoked → 401 → the API's own sign-out path. A 4009 is
 * therefore never treated as a sign-out here.
 *
 * Cadence (DEC-079, ERPat parity): a 4009 (this socket revoked on purpose)
 * re-bootstraps after the 2–5 s jitter, every time. Any OTHER refusal (a
 * refused key, a quota, an unsupported protocol) re-bootstraps first after the
 * jitter and then at most once per `REFUSED_REBOOTSTRAP_MIN_INTERVAL_MS` —
 * `max(jitter, interval − since the last one)` — retried on that cadence, never
 * once and then abandoned. The single `refusedTimer` guard stays: pusher-js
 * emits two `error` events per close.
 */
function handleRefusedClose(gen: number, code: number): void {
  if (refusedTimer !== null || gen !== generation) return;
  const store = useRealtimeStore();
  teardownSocket();
  stopIdleDrain();
  store.setStatus('connecting');
  store.setError('connection_refused');
  startPollFallback();
  const revoked = code === CLOSE_CODE_REVOKED;
  refusedRevoked = revoked;
  let delay = refusedRebootstrapDelayMs();
  if (!revoked && lastRefusedRebootstrapAt > 0) {
    delay = Math.max(delay, REFUSED_REBOOTSTRAP_MIN_INTERVAL_MS - (Date.now() - lastRefusedRebootstrapAt));
  }
  refusedTimer = setTimeout(() => {
    refusedTimer = null;
    refusedRevoked = false;
    if (!active || gen !== generation) return;
    if (!revoked) lastRefusedRebootstrapAt = Date.now();
    void bootstrapAndConnect(gen);
  }, delay);
}

/**
 * Whether a wake may re-bootstrap NOW instead of waiting for `refusedTimer`:
 * always after a 4009 (only its jitter is pending); after any other refusal
 * only once the DEC-079 interval since the last re-bootstrap has elapsed — the
 * first re-bootstrap after such a refusal always waits out its jitter.
 */
function wakeMayCollapseRefusal(): boolean {
  if (refusedRevoked) return true;
  return lastRefusedRebootstrapAt > 0 && Date.now() - lastRefusedRebootstrapAt >= REFUSED_REBOOTSTRAP_MIN_INTERVAL_MS;
}

/* ── Frames ────────────────────────────────────────────────────────────────── */

/** What the frame gate did with one item — a drain counts its rejections and reconciles once at the end. */
type FrameOutcome = 'accepted' | 'duplicate' | 'rejected';

/** The gateway contract's tenant scope + recipient for its operator test (`gateway_test_notification`). */
const GATEWAY_TEST_TENANT_SCOPE = 't_gateway_test';
const GATEWAY_TEST_RECIPIENT_ID = '0';

/**
 * The gateway operator test (the `gateway_test_notification` golden fixture):
 * a `notification.created` from the `system` actor, tenant `t_gateway_test`,
 * recipient `"0"`, and a UUID notification id (never a decimal one, so no
 * store ever holds it). It is exempt from the session-binding drop (DEC-100)
 * and from nothing else.
 */
function isGatewayTestEnvelope(envelope: RealtimeEnvelope): boolean {
  return (
    envelope.type === 'notification.created' &&
    envelope.tenant_scope === GATEWAY_TEST_TENANT_SCOPE &&
    envelope.recipient_id === GATEWAY_TEST_RECIPIENT_ID &&
    envelope.actor?.id === 'system' &&
    !/^\d+$/.test(envelope.notification?.id ?? '')
  );
}

/**
 * The single gate for a live frame AND a drained stream item. `drained`
 * envelopes are counted and applied but never toast or alert. A live frame
 * the parser rejects, or that is bound to another tenant / recipient, asks
 * for a throttled REST reconcile (§3.1); a drained one only counts — the
 * drain reconciles once when it ends. A duplicate is never a reconcile.
 */
function handleFrame(data: unknown, drained: boolean): FrameOutcome {
  const store = useRealtimeStore();
  const envelope = parseRealtimeEnvelope(data);
  if (!envelope) {
    // Unknown version/type or malformed: not rendered. The reason is a rule
    // name, never payload text.
    const reason = explainRealtimeEnvelopeRejection(data);
    if (IS_DEV) console.debug('[realtime] frame ignored:', reason);
    store.bump('rejected');
    store.setLastRejectReason(reason);
    if (!drained) reconcile(reason === 'type unknown' ? 'unknown_type' : 'rejected');
    return 'rejected';
  }
  // Session binding: a frame addressed to another tenant or recipient than
  // the one this socket was bootstrapped for is dropped, whatever channel it
  // came in on. (A company switch bumps the session epoch and restarts us,
  // so this is the belt to that brace.) The ONE exception is the gateway's
  // operator test (DEC-100): the gateway contract stamps it `t_gateway_test`
  // / recipient `"0"` on purpose and asks clients to render it through the
  // normal path — the ERPat web client toasts it — and its UUID notification
  // id keeps it out of every store, so nothing but a toast can come of it.
  if (
    binding &&
    !isGatewayTestEnvelope(envelope) &&
    (envelope.tenant_scope !== binding.tenantScope || envelope.recipient_id !== binding.recipientId)
  ) {
    if (IS_DEV) console.debug('[realtime] frame ignored: session mismatch');
    store.bump('rejected');
    store.setLastRejectReason('session_mismatch');
    if (!drained) reconcile('session_mismatch');
    return 'rejected';
  }
  if (recent.has(envelope.event_id)) {
    store.bump('duplicates');
    return 'duplicate';
  }
  recent.add(envelope.event_id);

  store.bump('accepted');
  store.markEvent();
  advanceAppliedSequence(envelope.sequence);
  void maybeRecordReceived(envelope.event_id);
  if (
    !drained &&
    (envelope.type === 'notification.created' ||
      // Never alert for a message you sent yourself. The boundary's hidden-only
      // rule does not help here — hidden is exactly when it fires, so sending
      // from ERPat web while this device is backgrounded would raise an OS
      // notification for your own message. Scoped to `message.created`: a
      // `notification.created` you are the actor of (an approval you performed)
      // is still intended.
      (envelope.type === 'message.created' && !isSelfActor(envelope)))
  ) {
    // Device alert per event (the OS collapses duplicates by id); shown only
    // while the app is hidden — the boundary enforces that rule.
    notificationsDevice.show(envelope);
  }
  queueDispatch(envelope, drained);
  return 'accepted';
}

/* ── Coalesced dispatch ────────────────────────────────────────────────────── */

/**
 * Per envelope type, one trailing window: a burst of ten frames (a bulk
 * approval, a drain) triggers ONE store refresh, ONE repaint tick and — for
 * live frames — one toast, not ten.
 */
function queueDispatch(envelope: RealtimeEnvelope, drained: boolean): void {
  const existing = bursts.get(envelope.type);
  if (existing) {
    existing.last = envelope;
    insertBySequence(existing.all, envelope);
    if (!drained) existing.live.push(envelope);
    return;
  }
  bursts.set(envelope.type, {
    live: drained ? [] : [envelope],
    all: [envelope],
    last: envelope,
    timer: setTimeout(() => flushBurst(envelope.type), DISPATCH_COALESCE_MS),
  });
}

/** Keep a burst's `all` in stream order — a drained page and a live frame may interleave. */
function insertBySequence(list: RealtimeEnvelope[], envelope: RealtimeEnvelope): void {
  let index = list.length;
  while (index > 0) {
    const previous = list[index - 1];
    if (!previous || compareStreamSequence(previous.sequence, envelope.sequence) <= 0) break;
    index -= 1;
  }
  list.splice(index, 0, envelope);
}

/**
 * Hand a burst to the reducers (every accepted envelope, in `sequence` order —
 * phase6-contract.md §3.3), refresh a store from REST ONLY when one of its
 * reductions could not be resolved locally, toast the live ones, and publish
 * the repaint tick. The stores are authoritative: no unconditional refresh
 * per burst any more.
 */
function flushBurst(type: RealtimeEnvelopeType): void {
  const burst = bursts.get(type);
  bursts.delete(type);
  if (!burst || !active) return;

  const store = useRealtimeStore();
  const notifications = useNotificationsStore();
  const messages = useMessagesStore();
  let refreshNotifications = false;
  let refreshMessages = false;
  const count = (outcome: ReducerOutcome): boolean => {
    if (outcome === 'stale') store.bump('stale');
    return outcome === 'unresolved';
  };
  for (const envelope of burst.all) {
    if (count(notifications.applyEvent(envelope))) refreshNotifications = true;
    if (count(messages.applyEvent(envelope))) refreshMessages = true;
  }
  if (refreshNotifications) void notifications.refresh();
  if (refreshMessages) void messages.refresh();

  const live = burst.live;
  const first = live[0];

  switch (type) {
    case 'notification.created':
      if (live.length === 1 && first) void showNotificationToast(first);
      else if (live.length > 1) void showBurstToast(live.length);
      break;
    case 'message.created': {
      // Two suppressions, applied as a FILTER over the window rather than a
      // guard on its last envelope:
      //   - your own message (the server sends the sender a sender-sync copy;
      //     there is no socket_id exclusion, so this is the client's job);
      //   - the thread already on screen (the page repaints on the tick below,
      //     so a toast would only nag).
      // Filtering matters: a 400ms window holding [someone's message, your own
      // echo] must still toast for theirs. Testing only the newest envelope
      // would swallow it.
      const notify = live.filter((e) => !isSelfActor(e) && !messages.isEnvelopeThreadOpen(e));
      const newest = notify[notify.length - 1];
      if (newest) void showMessageToast(newest);
      break;
    }
    case 'notification.read':
    case 'notification.dismissed':
    case 'message.read':
      // State changed on another device / ERPat web — the reducers applied it
      // above (or asked for the one refresh). Nothing to show.
      break;
  }

  store.tick({
    type,
    resource: burst.last.resource,
    thread: threadOf(burst.last),
    drained: live.length === 0,
  });
}

function clearBursts(): void {
  for (const burst of bursts.values()) clearTimeout(burst.timer);
  bursts.clear();
}

/** The thread a message envelope is about, from its (allowlisted) action. */
function threadOf(envelope: RealtimeEnvelope): RealtimeTick['thread'] {
  const action = envelope.action;
  if (!action || action.route !== 'messages.thread') return null;
  const id = action.params.thread_id?.trim() ?? '';
  const type = action.params.thread_type;
  if (!id || (type !== 'direct' && type !== 'group')) return null;
  return { id, type };
}

/*
 * "Is the user already looking at this conversation?" lives in
 * `messages.store.ts` as `isEnvelopeThreadOpen()`, NOT here.
 *
 * The private copy this file used to hold compared the envelope's `message_id`
 * / `resource.id` against the route param. Those are the NEW message's id,
 * while a direct conversation is routed by the thread's ROOT message id — so it
 * matched only on a thread's very first message and every reply toasted over
 * the conversation the user was reading. The store resolves the thread properly
 * (through the counterpart index), and sharing its body is what keeps the toast
 * and the unread count from ever disagreeing.
 */

/* ── In-app toasts ─────────────────────────────────────────────────────────── */

/**
 * The shared toast renders `title`/`message` as TEXT (Ionic's
 * `innerHTMLTemplatesEnabled` is off — see main.ts), which is what keeps a
 * `<img onerror>` title inert.
 *
 * The action goes through the ONE router entry for realtime targets (§3.7):
 * the tenant stamp and user id are captured when the toast is shown, so a tap
 * after a company switch or a sign-out is refused or parked rather than
 * landing on a foreign tenant's page. A successful navigation records the
 * `opened` receipt itself (`recordOpened`).
 */
async function presentToast(
  title: string,
  body: string,
  target: RouteLocationRaw,
  eventId: string | null,
  overrides: Pick<ToastOverrides, 'position'> = {},
): Promise<void> {
  const context = { tenant: tenantStamp(), userId: getSessionScope().userId, eventId };
  // Before the awaited present(), so the sound leads the animation rather than
  // trailing it. This is the ONE audio seam: every realtime toast comes through
  // here, so the chime inherits the sender / open-thread / drained suppressions
  // above for free, and it can never reach the app's other toasts (a saved
  // form, a deleted row). It no-ops unless the document is visible, which is
  // the exact complement of the hidden-only device alert.
  playNotificationChime();
  try {
    await useToast().show(body || title, {
      ...(body ? { title } : {}),
      duration: NOTIFICATION_TOAST_DURATION_MS,
      position: overrides.position ?? 'bottom',
      action: {
        text: notificationCopy('notifications.settings.toastAction'),
        handler: () => {
          navigateToRealtimeTarget(target, context);
        },
      },
    });
  } catch {
    /* a missed toast must never break frame handling */
  }
}

async function showNotificationToast(envelope: RealtimeEnvelope): Promise<void> {
  const fallback = privateFallbackCopy();
  const notification = envelope.notification;
  const title = notification?.title.trim() || fallback.title;
  const body = notification?.body.trim() || (notification?.title.trim() ? '' : fallback.body);
  await presentToast(title, body, resolveRealtimeTarget(envelope), envelope.event_id);
}

async function showBurstToast(count: number): Promise<void> {
  const copy = burstCopy(count);
  await presentToast(copy.title, copy.body, INBOX_LOCATION, null);
}

async function showMessageToast(envelope: RealtimeEnvelope): Promise<void> {
  // Message events are content-free on the wire by policy: fixed copy only.
  //
  // TOP, unlike the other two: every `/tabs/messages/…` path is a "composer
  // route", so a bottom toast is lifted 96px to clear the chat composer —
  // landing squarely over the last messages and under the user's typing thumb.
  const copy = messageAlertCopy();
  await presentToast(copy.title, copy.body, resolveRealtimeTarget(envelope), envelope.event_id, {
    position: 'top',
  });
}

/* ── Cursor persistence ────────────────────────────────────────────────────── */

function loadCursor(key: string): string | null {
  try {
    return localStorage.getItem(key) || null;
  } catch {
    return null;
  }
}

function setCursor(next: string): void {
  cursor = next;
  if (binding) {
    try {
      localStorage.setItem(binding.cursorKey, next);
    } catch {
      /* storage unavailable — the in-memory cursor still serves this session */
    }
  }
  useRealtimeStore().setHasCursor(true);
}

function clearCursor(): void {
  cursor = null;
  if (binding) {
    try {
      localStorage.removeItem(binding.cursorKey);
    } catch {
      /* nothing to remove */
    }
  }
  useRealtimeStore().setHasCursor(false);
}

/**
 * The sign-out clear (DEC-083): the binding's own key when one is held, plus
 * — defensively — every key under the cursor prefix. The key is built from
 * the server's `tenant_scope` (not the company key), so the session snapshot
 * alone cannot rebuild it, and on the expiry path the token is already gone:
 * the prefix sweep is the clear that needs no identity at all, and it covers
 * the snapshot's user by construction. Nothing signed per user survives a
 * sign-out on a shared device.
 */
function forgetPersistedCursors(): void {
  cursor = null;
  const keys = new Set<string>();
  if (binding) keys.add(binding.cursorKey);
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(`${CURSOR_STORAGE_PREFIX}:`)) keys.add(key);
    }
    for (const key of keys) localStorage.removeItem(key);
  } catch {
    /* storage unavailable — nothing persisted to forget */
  }
  useRealtimeStore().setHasCursor(false);
}

/* ── Catch-up drain ────────────────────────────────────────────────────────── */

/**
 * Walk `GET /me/notifications/changes` from the cursor until the server says
 * there is nothing more. Serialized: a second call while one runs is folded
 * into one follow-up pass.
 */
async function drain(gen: number): Promise<void> {
  if (!active || gen !== generation || !binding) return;
  if (draining) {
    drainQueued = true;
    return;
  }
  const store = useRealtimeStore();
  if (cursor === null) {
    // No position to walk from (a stream the server did not sign, or a refused
    // one waiting for its re-bootstrap): never ask for "everything" — say the
    // gap is possible and let the next bootstrap hand out a cursor (ERPat parity).
    store.setSyncState('degraded');
    return;
  }
  draining = true;
  store.setSyncState('syncing');
  const epoch = getSessionEpoch();
  let outcome: 'done' | 'abandoned' | 'rebootstrap' | 'refused' = 'abandoned';
  let rejections = 0;

  try {
    let pages = 0;
    while (pages < DRAIN_MAX_PAGES) {
      const page = await fetchChanges(cursor, CHANGES_PAGE_SIZE);
      if (!isCurrent(gen, epoch)) return;
      for (const raw of page.events) {
        if (handleFrame(raw, true) === 'rejected') rejections += 1;
      }
      if (page.nextCursor) setCursor(page.nextCursor);
      pages += 1;
      if (!page.hasMore) {
        if (page.unreadCounts) applyUnreadCounts(page.unreadCounts, page.countsAsOfSequence);
        drainRetryAttempt = 0;
        cursorRebootstraps = 0;
        store.bump('drains');
        store.setSyncState('synchronized');
        outcome = 'done';
        return;
      }
    }
    // Still behind after the page budget: a fresh snapshot beats replaying more.
    outcome = 'rebootstrap';
  } catch (err) {
    if (!isCurrent(gen, epoch)) return;
    if (err instanceof RealtimeApiError) {
      if (
        err.code === 'session_expired' ||
        err.code === 'session_changed' ||
        err.code === 'no_session' ||
        err.code === 'aborted'
      ) {
        return;
      }
      if (err.status === 403 || err.status === 404 || err.status === 410 || err.status === 422) {
        // The server REFUSED this page (`cursor_expired`, `invalid_cursor`,
        // `module_unavailable`, forbidden): the position is unusable here. The
        // cursor is NOT dropped yet — it goes when the re-bootstrap actually
        // runs and hands out a fresh one (DEC-080, see the `finally`).
        outcome = 'refused';
        return;
      }
      if (err.status === 0 && isOffline.value) {
        // Offline: not a retry, not a strike — the reachability edge drains.
        store.setSyncState('degraded');
        return;
      }
    }
    // Network / 5xx: bounded back-off, then admit the gap.
    drainRetryAttempt += 1;
    if (drainRetryAttempt > DRAIN_RETRY_ATTEMPTS) {
      drainRetryAttempt = 0;
      store.setSyncState('degraded');
      reconcile('drain_failed');
      return;
    }
    scheduleDrainRetry(gen);
  } finally {
    draining = false;
    if (rejections > 0) {
      store.bump('drainRejections', rejections);
      if (active && gen === generation) reconcile('drain_rejections');
    }
    if (!active || gen !== generation) {
      drainQueued = false;
    } else if (outcome === 'refused') {
      drainQueued = false;
      const now = Date.now();
      if (now - lastDrainRefusalRebootstrapAt < DRAIN_REFUSAL_REBOOTSTRAP_MIN_INTERVAL_MS) {
        // Refused again inside the window: the server keeps saying no. Stay
        // degraded (the poller covers the gap; the idle drain, a reconnect or a
        // reconcile tries again) instead of looping bootstrap → drain → 4xx.
        store.setSyncState('degraded');
        startPollFallback();
      } else {
        cursorRebootstraps += 1;
        if (cursorRebootstraps > 2) {
          // Two fresh cursors in a row still refused: the outer bound.
          cursorRebootstraps = 0;
          store.setSyncState('degraded');
          startPollFallback();
        } else {
          lastDrainRefusalRebootstrapAt = now;
          dropCursorOnBind = true;
          teardownSocket();
          stopIdleDrain();
          void bootstrapAndConnect(gen);
        }
      }
    } else if (outcome === 'rebootstrap') {
      drainQueued = false;
      cursorRebootstraps += 1;
      if (cursorRebootstraps > 2) {
        // Two fresh cursors in a row still unusable: stop looping on the
        // server and admit the gap rather than hammer bootstrap.
        cursorRebootstraps = 0;
        store.setSyncState('degraded');
      } else {
        teardownSocket();
        stopIdleDrain();
        void bootstrapAndConnect(gen);
      }
    } else if (drainQueued) {
      drainQueued = false;
      void drain(gen);
    }
  }
}

function scheduleDrainRetry(gen: number): void {
  clearDrainRetryTimer();
  drainRetryTimer = setTimeout(() => {
    drainRetryTimer = null;
    if (!active || gen !== generation) return;
    void drain(gen);
  }, drainRetryDelayMs(drainRetryAttempt));
}

function clearDrainRetryTimer(): void {
  if (drainRetryTimer !== null) {
    clearTimeout(drainRetryTimer);
    drainRetryTimer = null;
  }
}

function startIdleDrain(gen: number): void {
  stopIdleDrain();
  idleDrainTimer = setInterval(() => {
    if (!active || gen !== generation) return;
    if (pusher?.connection.state !== 'connected') return;
    void drain(gen);
  }, IDLE_DRAIN_INTERVAL_MS);
}

function stopIdleDrain(): void {
  if (idleDrainTimer !== null) {
    clearInterval(idleDrainTimer);
    idleDrainTimer = null;
  }
}

/**
 * Adopt the server's unread totals (a snapshot's `as_of_sequence`, a changes
 * page's `counts_as_of_sequence`) — only when they are at least as new as
 * everything already applied; an older count cannot describe the newer state.
 * The notifications store makes it the badge's server term (adjusted by newer
 * local transitions); the messages store records it and reloads the list on a
 * mismatch with its projected total (the count is never the messages badge).
 */
function applyUnreadCounts(counts: RealtimeUnreadCounts, asOf: string): void {
  if (!isAtOrPastApplied(asOf)) return;
  useNotificationsStore().setServerUnread(counts.notifications, asOf);
  useMessagesStore().setServerUnread(counts.messages, asOf);
  advanceAppliedSequence(asOf);
}

/* ── Receipts ──────────────────────────────────────────────────────────────── */

/** RFC 3339 UTC, second precision — what the receipts endpoint validates. */
function nowSeconds(): string {
  return new Date().toISOString().replace(/\.\d{3}Z$/, 'Z');
}

/**
 * `client_received` is SAMPLED per installation: sha256(event_id + "\n" +
 * installation) → the first 4 bytes as a fraction of 2^32 < sample rate.
 * Deterministic, so a redelivered event is either always or never reported.
 */
async function shouldSample(eventId: string, installation: string, rate: number): Promise<boolean> {
  if (rate <= 0) return false;
  if (rate >= 1) return true;
  const subtle = typeof crypto !== 'undefined' ? crypto.subtle : undefined;
  if (!subtle) return false;
  try {
    const digest = await subtle.digest('SHA-256', new TextEncoder().encode(`${eventId}\n${installation}`));
    return new DataView(digest).getUint32(0) / 2 ** 32 < rate;
  } catch {
    return false;
  }
}

async function maybeRecordReceived(eventId: string): Promise<void> {
  const bound = binding;
  if (!bound || !bound.receipts || !bound.installation) return;
  if (!(await shouldSample(eventId, bound.installation, bound.sampleRate))) return;
  if (!active || binding !== bound) return;
  recordReceipt(eventId, 'client_received');
}

function recordReceipt(eventId: string, type: RealtimeReceipt['type']): void {
  if (!active || !binding?.receipts) return;
  receiptQueue.push({ eventId, type, observedAt: nowSeconds() });
  if (receiptQueue.length >= RECEIPT_BATCH_MAX) {
    void flushReceipts();
    return;
  }
  if (receiptTimer === null) {
    receiptTimer = setTimeout(() => {
      receiptTimer = null;
      void flushReceipts();
    }, RECEIPT_FLUSH_INTERVAL_MS);
  }
}

async function flushReceipts(): Promise<void> {
  if (receiptTimer !== null) {
    clearTimeout(receiptTimer);
    receiptTimer = null;
  }
  if (!active) {
    receiptQueue.length = 0;
    return;
  }
  if (receiptQueue.length === 0) return;
  const batch = receiptQueue.splice(0, RECEIPT_BATCH_MAX);
  try {
    await postReceipts(batch);
  } catch {
    /* a failed batch is dropped by contract — receipts are telemetry */
  }
  if (active && receiptQueue.length > 0) void flushReceipts();
}

function clearReceipts(): void {
  if (receiptTimer !== null) {
    clearTimeout(receiptTimer);
    receiptTimer = null;
  }
  receiptQueue.length = 0;
}

/* ── Fallback polling while the socket is down ─────────────────────────────── */

function startPollFallback(): void {
  if (pollTimer !== null) return;
  pollTimer = setInterval(() => {
    if (!active || typeof document === 'undefined' || document.visibilityState !== 'visible') return;
    void useNotificationsStore().refresh();
    void useMessagesStore().refresh();
  }, pollIntervalMs);
}

function stopPollFallback(): void {
  if (pollTimer !== null) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

/* ── Wake-ups ──────────────────────────────────────────────────────────────── */

/**
 * On return to the foreground / network, nudge whichever stage is stalled:
 * a pending bootstrap retry (or refused-close re-bootstrap) runs now instead
 * of at its scheduled time, a dropped socket is asked to reconnect
 * immediately (pusher-js's own retry timer may be minutes out after a long
 * background stretch), and a connected socket drains — a backgrounded tab may
 * have been frozen with frames it never saw.
 */
function nudge(): void {
  if (!active) return;
  if (refusedTimer !== null) {
    // Never `connect()` a socket the server refused — re-bootstrap instead.
    // A wake collapses a 4009's jitter; after any other refusal it may only
    // run the re-bootstrap once the DEC-079 interval has elapsed (the timer
    // keeps the cadence otherwise).
    if (!wakeMayCollapseRefusal()) return;
    clearRefusedTimer();
    if (!refusedRevoked) lastRefusedRebootstrapAt = Date.now();
    refusedRevoked = false;
    void bootstrapAndConnect(generation);
    return;
  }
  if (!pusher) {
    if (retryTimer !== null) {
      // Run the pending bootstrap retry now (keep the backoff position).
      clearRetryTimer();
      void bootstrapAndConnect(generation);
      return;
    }
    // A `disabled` verdict (tenant off, version mismatch, a server without the
    // endpoints) is not permanent, but nothing else would ever ask again — the
    // service keeps `active` with no socket and no timer — so re-check it on
    // return to the foreground/network instead of only after a sign-out. A
    // bootstrap already in flight reports `connecting`, so this cannot double
    // up. A FLAG-disabled build never asks: it polls and opens no socket. A
    // `gateway_loopback` refusal (DEC-102) is re-checked the same way — through
    // its explicit latch, not the `failed` status, which pusher-js also reports
    // — so a corrected server is picked up on the next foreground / network wake.
    if (!flagDisabled && (useRealtimeStore().status === 'disabled' || gatewayRefused)) {
      void bootstrapAndConnect(generation);
    }
    return;
  }
  const state = pusher.connection.state;
  if (state === 'unavailable' || state === 'disconnected' || state === 'failed') {
    pusher.connect();
    return;
  }
  if (state === 'connected') void drain(generation);
}

function onVisibilityChange(): void {
  if (document.visibilityState === 'visible') nudge();
}

/* ── Offline pause / reachability resume (§3.1) ─────────────────────────── */

/**
 * The API is unreachable (the `isOffline` true edge, or the browser's own
 * `offline`): stop asking. The drain retry and the receipt timer are cleared
 * (the receipt QUEUE is kept — it goes out on resume), the REST poll stops,
 * and a drain that was mid-way is admitted as a possible gap. pusher-js keeps
 * its own reconnect loop; a refused-close or bootstrap timer is left alone —
 * a bootstrap that fails offline backs off on its own and `nudge()` runs it
 * early on resume.
 */
function pauseForOffline(): void {
  if (!active) return;
  clearDrainRetryTimer();
  if (receiptTimer !== null) {
    clearTimeout(receiptTimer);
    receiptTimer = null;
  }
  stopPollFallback();
  const store = useRealtimeStore();
  if (store.syncState === 'syncing') store.setSyncState('degraded');
}

/**
 * Reachable again (the `isOffline` false edge, or `online`): nudge whichever
 * stage stalled, post the receipts kept while offline, and let the outbox
 * deliver what was queued (the store throttles that walk).
 */
function resumeFromOffline(): void {
  if (!active) return;
  nudge();
  void flushReceipts();
  useMessagesStore().flushOutbox();
}

function onOnline(): void {
  resumeFromOffline();
}

function onOffline(): void {
  pauseForOffline();
}

function attachWakeListeners(): void {
  if (wakeListenersAttached || typeof document === 'undefined') return;
  wakeListenersAttached = true;
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  // The connectivity signal is the trustworthy one (it pings the API): pause
  // on its true edge, resume on its false edge — even when the browser never
  // fired `online` (lie-fi, a captive portal that let go).
  offlineWatchStop = watch(isOffline, (offline, was) => {
    if (offline && !was) pauseForOffline();
    else if (!offline && was) resumeFromOffline();
  });
}

function detachWakeListeners(): void {
  if (!wakeListenersAttached) return;
  wakeListenersAttached = false;
  document.removeEventListener('visibilitychange', onVisibilityChange);
  window.removeEventListener('online', onOnline);
  window.removeEventListener('offline', onOffline);
  if (offlineWatchStop) {
    offlineWatchStop();
    offlineWatchStop = null;
  }
}
